import { useState } from 'react';
import { useIssues } from '../context/IssueContext';
import { Issue, IssueFormData } from '../types/issue';
import { toast } from 'react-hot-toast';

export const useIssueManagement = () => {
  const {
    issues,
    loading: issuesLoading,
    error: issuesError,
    fetchIssues,
    createIssue,
    updateIssue,
    deleteIssue,
    publishIssue,
    archiveIssue,
  } = useIssues();

  const [issueFormOpen, setIssueFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'add' | 'edit'>('add');
  const [selectedIssue, setSelectedIssue] = useState<IssueFormData | null>(null);

  const handleAddIssue = () => {
    setFormMode('add');
    setSelectedIssue(null);
    setIssueFormOpen(true);
  };

  const handleEditIssue = (issue: Issue) => {
    setSelectedIssue(convertIssueToFormData(issue));
    setFormMode('edit');
    setIssueFormOpen(true);
  };

  const handleDeleteIssue = async (id: number) => {
    try {
      await deleteIssue(id);
      await fetchIssues();
      toast.success('Выпуск успешно удален');
    } catch (error) {
      console.error('Ошибка при удалении выпуска:', error);
      toast.error('Ошибка при удалении выпуска');
    }
  };

  const handleIssueSubmit = async (data: IssueFormData) => {
    try {
      if (formMode === 'add') {
        await createIssue(data);
        toast.success('Выпуск создан');
      } else if (selectedIssue && selectedIssue.id) {
        const issueId = typeof selectedIssue.id === 'string' ? parseInt(selectedIssue.id) : selectedIssue.id;
        await updateIssue(issueId, data);
        toast.success('Выпуск обновлен');
      }
      await fetchIssues();
      setIssueFormOpen(false);
    } catch (error) {
      console.error('Ошибка при сохранении выпуска:', error);
      toast.error('Ошибка при сохранении выпуска');
    }
  };

  const handlePublishIssue = async (id: number) => {
    try {
      await publishIssue(id);
      await fetchIssues();
      toast.success('Выпуск опубликован');
    } catch (error) {
      console.error('Ошибка при публикации выпуска:', error);
      toast.error('Ошибка при публикации выпуска');
    }
  };

  const handleArchiveIssue = async (id: number) => {
    try {
      await archiveIssue(id);
      await fetchIssues();
      toast.success('Выпуск архивирован');
    } catch (error) {
      console.error('Ошибка при архивации выпуска:', error);
      toast.error('Ошибка при архивации выпуска');
    }
  };

  const convertIssueToFormData = (issue: Issue): IssueFormData => ({
    id: issue.id,
    title: issue.title,
    description: issue.description,
    volume: issue.volume,
    number: issue.number,
    year: issue.year,
    status: issue.status,
    topics: issue.topics || [],
    coverImage: issue.coverImage
  });

  return {
    issues,
    issuesLoading,
    issuesError,
    issueFormOpen,
    formMode,
    selectedIssue,
    setIssueFormOpen,
    handleAddIssue,
    handleEditIssue,
    handleDeleteIssue,
    handleIssueSubmit,
    handlePublishIssue,
    handleArchiveIssue,
  };
}; 